<template>
  <div class="coupon-container">
    <div class="card">
      <div class="top-header-wrapper">
        <button @click="close">
          <img src="@/assets/icons/primary-back.svg" alt="back">
        </button>
        <p class="title">{{ t('myCoupon.title') }}</p>
      </div>
      <div v-if="coupons.length === 0" class="no-coupon-wrapper">
        <img src="@/assets/images/no-coupon.svg" alt="no-coupon">
        <p class="no-coupon-text">{{ t('myCoupon.noItem') }}</p>
      </div>
      <div v-else-if="coupons.length > 0" class="coupon-panel">
        <div class="coupon-wrapper">
          <CouponCard
            v-for="coupon in coupons"
            :key="coupon.id"
            :coupon="couponCardProps(coupon)"
            mode="cart"
            @action="handleCouponAction"
          />
        </div>
        <div class="action-bar">
          <MButton :disabled="!selectedCoupon" class="action-btn" @click="close">{{  t('submit') }}</MButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

import CouponCard from '@/components/CouponCard'
import MButton from '@/components/Form/MButton'

export default {
  name: 'CouponView',
  props: {
    back: {
      type: Function,
    }
  },
  components: {
    CouponCard,
    MButton,
  },
  setup(props) {
    /** context */
    const store = useStore()
    const { t } = useI18n()

    /** computed */
    const selectedCoupon = computed(() => store.state.cart.data.coupon_code)

    // FIX TASK 4: Sort coupons consistently instead of showing in random DB order.
    // Sort order:
    //   1. Currently selected coupon always first
    //   2. Then by discount_amount descending (highest discount at top)
    // This prevents the "strange ordering" caused by no ORDER BY on the backend query.
    const coupons = computed(() => {
      const raw = store.state.cart.data?.coupon_promotions || []
      const selected = selectedCoupon.value
      return [...raw].sort((a, b) => {
        if (a.code === selected) return -1
        if (b.code === selected) return 1
        return Number(b.discount_amount) - Number(a.discount_amount)
      })
    })

    /** method */
    const close = () => props.back()
    const couponCardProps = coupon => ({
      ...coupon,
      isSelected: selectedCoupon.value === coupon.code,
    })
    const handleCouponAction = async (coupon) => {
      if (!coupon) {
        return
      }
      await store.dispatch('cart/fetchApplyCoupon', coupon.code)
    }

    return {
      t,
      coupons,
      selectedCoupon,
      close,
      couponCardProps,
      handleCouponAction,
    }
  }
}
</script>

<style lang="scss" scoped>
.title {
  font-size: 24px;
  font-weight: bold;
}
.coupon-container {
  position: fixed;
  z-index: 900;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #CFCFCFCC;
}

.card {
  background-color: #FFFFFF;
  width: min(760px, calc(100vw - 32px));
  max-height: min(80vh, 760px);
  padding: 16px;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  @media screen and (max-width: 779px) {
    width: calc(100vw - 24px);
    max-height: calc(100vh - 24px);
    padding: 12px;
    border-radius: 20px;
  }
}

.coupon-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.top-header-wrapper {
  display: flex;
  align-items: flex-end;
  padding: 16px;
  background-color: var(--white-color);
  gap: 16px;
  > button {
    font-weight: bold;
    color: var(--secondary-color);
    outline: none;
    background-color: transparent;
    border: 0;
  }
}

.coupon-wrapper {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 16px;
  gap: 16px;
  min-height: 0;
  flex: 1;
}

.no-coupon-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.no-coupon-text {
  font-size: 16px;
  margin: 32px 0;
}

.action-bar {
  flex-shrink: 0;
  padding: 12px 16px 0;
  background-color: var(--white-color);
}

.action-btn {
  width: 100%;
}

</style>
